/**
 * Flavor Banner — compact colored strip showing non-prod environment.
 *
 * Display map (locked):
 * - Metro / unnamed Debug → LOCAL
 * - Android/iOS `dev` flavor → DQ
 * - `qa` flavor → QA
 * - `secure` flavor → SECURE DEV
 * - PROD → hidden
 */

import React, { useEffect, useState } from 'react';
import { Platform, StatusBar, View, Text, StyleSheet } from 'react-native';
import DeviceInfo from 'react-native-device-info';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { isAppUpdateRoute } from '../constants/appUpdate';
import {
  devGitBranch,
  devGitShortSha,
  devGitWorktreeTag,
} from '../config/devBuildMeta.generated';

export type Flavor = 'LOCAL' | 'DQ' | 'QA' | 'SECURE' | 'HOT' | 'PROD';

const BANNER_ALPHA = 0.55;

const LABELS: Record<Flavor, string> = {
  LOCAL: '💻 LOCAL',
  DQ: '🔧 DQ',
  QA: '🧪 QA',
  SECURE: '🔒 SECURE DEV',
  HOT: '🔥 HOT',
  PROD: '🚀 PROD',
};

function flavorColor(flavor: Flavor): string {
  switch (flavor) {
    case 'LOCAL':
      return `rgba(142, 68, 173, ${BANNER_ALPHA})`;
    case 'DQ':
      return `rgba(255, 149, 0, ${BANNER_ALPHA})`;
    case 'QA':
      return `rgba(0, 122, 255, ${BANNER_ALPHA})`;
    case 'SECURE':
      return `rgba(230, 126, 34, ${BANNER_ALPHA})`;
    case 'HOT':
      return `rgba(231, 76, 60, ${BANNER_ALPHA})`;
    case 'PROD':
      return `rgba(39, 174, 96, ${BANNER_ALPHA})`;
  }
}

function flavorLabel(flavor: Flavor): string {
  switch (flavor) {
    case 'LOCAL':
      return LABELS.LOCAL;
    case 'DQ':
      return LABELS.DQ;
    case 'QA':
      return LABELS.QA;
    case 'SECURE':
      return LABELS.SECURE;
    case 'HOT':
      return LABELS.HOT;
    case 'PROD':
      return LABELS.PROD;
  }
}

/**
 * Prefer flavor name / bundle id. Metro `__DEV__` without a flavor → LOCAL.
 * `dev` product flavor surfaces as **DQ** (not "DEV").
 */
export function detectFlavor(
  applicationName: string | null | undefined,
  bundleId: string | null | undefined,
  isDevBuild: boolean,
): Flavor {
  const lowerName = (applicationName || '').toLowerCase();
  const lowerBundle = (bundleId || '').toLowerCase();
  // Android: com.vertiv.its.dev · iOS: com.vertiv.its-dev
  const bundleHas = (suffix: string) =>
    lowerBundle.endsWith(`.${suffix}`) ||
    lowerBundle.endsWith(`-${suffix}`) ||
    lowerBundle.includes(`.${suffix}.`) ||
    lowerBundle.includes(`-${suffix}-`);

  if (lowerName.includes('local') || bundleHas('local')) {
    return 'LOCAL';
  }
  if (
    lowerName.includes('hot') ||
    lowerName.includes('staging') ||
    bundleHas('hot') ||
    bundleHas('staging')
  ) {
    return 'HOT';
  }
  if (lowerName.includes('secure') || bundleHas('secure')) {
    return 'SECURE';
  }
  if (lowerName.includes('qa') || bundleHas('qa')) {
    return 'QA';
  }
  // Product flavor `dev` / "Vertiv Dev" → DQ (not "DEV")
  if (lowerName.includes('dev') || bundleHas('dev')) {
    return 'DQ';
  }
  if (isDevBuild) {
    return 'LOCAL';
  }
  return 'PROD';
}

/** Metro local dev: unify DQ product flavor + unnamed debug under LOCAL. */
export function resolveBannerFlavor(
  detected: Flavor,
  isDevBuild: boolean,
): Flavor {
  if (isDevBuild && (detected === 'DQ' || detected === 'LOCAL')) {
    return 'LOCAL';
  }
  return detected;
}

function shortenBranch(branch: string): string {
  const trimmed = branch.trim();
  if (trimmed.length <= 32) {
    return trimmed;
  }
  return `${trimmed.slice(0, 29)}…`;
}

export function formatFlavorBannerText(
  flavor: Flavor,
  version: string,
  options?: {
    gitBranch?: string;
    gitShortSha?: string;
    gitWorktreeTag?: string;
    isDevBuild?: boolean;
  },
): string {
  const isDevBuild = options?.isDevBuild ?? false;
  const bannerFlavor = resolveBannerFlavor(flavor, isDevBuild);
  const parts = [flavorLabel(bannerFlavor)];

  if (
    isDevBuild &&
    bannerFlavor === 'LOCAL' &&
    options?.gitBranch &&
    options.gitBranch !== 'unknown'
  ) {
    parts.push(shortenBranch(options.gitBranch));
    if (options.gitShortSha) {
      parts.push(options.gitShortSha);
    }
    if (options.gitWorktreeTag) {
      parts.push(options.gitWorktreeTag);
    }
  }

  parts.push(`v${version}`);
  return parts.join(' • ');
}

export const FlavorBanner: React.FC = () => {
  const insets = useSafeAreaInsets();
  const [flavor, setFlavor] = useState<Flavor | null>(null);
  const [version, setVersion] = useState('');

  useEffect(() => {
    try {
      const name = DeviceInfo.getApplicationName();
      const bundleId = DeviceInfo.getBundleId();
      const detected = detectFlavor(name, bundleId, __DEV__);

      setFlavor(resolveBannerFlavor(detected, __DEV__));
      setVersion(DeviceInfo.getVersion() || '1.0.0');
      if (__DEV__) {
        console.info(
          `[Runtime build] ${devGitBranch} ${devGitShortSha} ${devGitWorktreeTag}`,
        );
      }

      if (Platform.OS === 'android' && detected !== 'PROD') {
        StatusBar.setTranslucent(true);
        StatusBar.setBackgroundColor('transparent');
        StatusBar.setBarStyle('light-content');
      }
    } catch (err) {
      if (__DEV__) {
        console.warn('[FlavorBanner] Error detecting flavor:', err);
        setFlavor('LOCAL');
        setVersion('1.0.0');
        return;
      }
      setFlavor('PROD');
    }
  }, []);

  if (
    Platform.OS === 'web' &&
    typeof window !== 'undefined' &&
    isAppUpdateRoute(window.location.pathname)
  ) {
    return null;
  }

  if (!flavor || flavor === 'PROD') {
    return null;
  }

  const color = flavorColor(flavor);
  const paddingTop = Math.max(insets.top, 0);

  return (
    <View
      style={[styles.banner, { backgroundColor: color, paddingTop }]}
      pointerEvents="none"
      testID="flavor-banner"
    >
      <Text style={styles.text}>
        {formatFlavorBannerText(flavor, version, {
          gitBranch: devGitBranch,
          gitShortSha: devGitShortSha,
          gitWorktreeTag: devGitWorktreeTag,
          isDevBuild: __DEV__,
        })}
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  banner: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 999999,
    elevation: 999999,
    paddingBottom: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  text: {
    color: '#FFF',
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '700',
    textShadowColor: 'rgba(0, 0, 0, 0.45)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
});

export default FlavorBanner;
